import styled, { keyframes } from 'styled-components';

import {
  green,
  red,
  black,
  lightGrayBackground,
  lightGreen,
  gray,
  lightGray,
  white,
  lighterOrange,
  border1,
} from 'utils/style/colors';
import Badge from '../Badge';
import SanitizedHTML from 'react-sanitized-html';
import { TaskStatus } from 'store/tasks/tasksType';

interface IItemWrapperProps {
  active: boolean;
  draft?: boolean;
  dependencyNotMeet?: boolean;
}

interface ITotalProps {
  width: number;
}

interface IVerifyStatusProps {
  status: TaskStatus;
}

interface IProgressProps {
  color: string;
}

interface ITaskTitleProps {
  deleted: boolean;
  archived: boolean;
}

export const draftBackground = // eslint-disable-next-line
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3E%3Cg fill='%23dbd9df' fill-opacity='0.4'%3E%3Cpath fill-rule='evenodd' d='M0 0h4v4H0V0zm4 4h4v4H4V4z'/%3E%3C/g%3E%3C/svg%3E\");";

export const dependencyNotMeetBackground = (fillColor: string) => `
  url("data:image/svg+xml;utf8,
    <svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'>
      <g fill='${fillColor}' fill-opacity='1'>
        <path fill-rule='evenodd' d='M0 0h4v4H0V0zm4 4h4v4H4V4z'/>
      </g>
    </svg>
  "),
  linear-gradient(to left, transparent, ${lighterOrange});
`;

export const BadgeItemWrapper = styled.div`
  padding: 1.7rem 1.3rem;
  width: 40rem;
  border-radius: 0 2rem 2rem 0;
  background-image: ${(props) =>
    !props.active
      ? `linear-gradient(to ${props.theme.direction === 'rtl' ? 'left' : 'right'}, ${props.badgeColorType} 0%, transparent 30%)`
      : `linear-gradient(to ${props.theme.direction === 'rtl' ? 'left' : 'right'}, ${props.badgeColorType} 0%, transparent 100%)`};
  color: var(--exact-white);
  overflow: hidden;
  display: flex;
  cursor: ${(props) => (props.isMentioned ? 'pointer' : 'auto')};
`;

export const ItemContianer = styled.div`
  overflow: hidden;
  display: flex;
  min-height: 5rem !important;
  align-items: stretch;
  justify-content: center;
  flex-direction: row !important;
  border-radius: 2rem;
  margin: 1rem 0;
  background-color: var(--exact-white);
  box-shadow: ${(props) => props.active && `0 0 1rem ${gray}`};
`;

export const ItemWrapper = styled('li')<IItemWrapperProps>`
  list-style: none !important;
  min-height: 5rem;
  width: 100%;
  position: relative;
  background-color: ${(props) =>
    props.active ? 'transparent' : 'var(--exact-white)'};
  background-image: ${(props) =>
    props.draft === true
      ? draftBackground
      : props.dependencyNotMeet === true
        ? dependencyNotMeetBackground(props.isDark ? 'black' : 'white')
        : ''};
  &:hover {
    background-color: transparent;
    ${(props) =>
      props.theme.direction === 'rtl'
        ? `
          border-bottom-right-radius: 0;
          border-top-right-radius: 0;
          border-right: 0.1rem solid transparent;
        `
        : `
          border-bottom-right-radius: 0;
          border-top-right-radius: 0;
          border-left: 0.1rem solid transparent;
        `}
  }
  border-radius: 2rem;
  border: ${(props) => (props.active ? 'none' : `0.1rem solid ${lightGray};`)};
  margin-inline-start: -35rem;
`;

const zoomInFromRight = keyframes` 
  0% {
    transform: translateX(100%);
}
  100% {
    transform: translateX(0);
}
`;
export const Total = styled('div')<ITotalProps>`
  width: ${(props) => props.width}%;
  height: 6px;
  background-color: hsl(${(props) => props.width * 1.73}, 59%, 49%);
  position: absolute;
  animation: 1s ease-out 0s 1 ${zoomInFromRight};
  top: 0;
  right: 0;
`;

export const TaskItem = styled('div')<IItemWrapperProps>`
  height: 100%;
  flex: 1;
  display: flex;
  position: relative;
  justify-content: center;
  flex-direction: column;
  padding: 0 0.5em;
  /* border-top: 1px solid #cccccc; */
  & > a {
    color: ${black};
    height: 100%;
    display: flex;
    align-items: center;
  }

  /* &:hover {
    background: ${(props) => (props.active ? lightGreen : lightGrayBackground)};
  } */
  overflow: hidden;
`;

export const BadgeGroup = styled('div')`
  gap: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const TaskTitleText = styled(SanitizedHTML)`
  //display: contents;
  @media (max-width: 500px) {
    display: block;
    margin-top: 1px;
    clear: both;
  }
`;
export const TaskTitle = styled('span')<ITaskTitleProps>`
  //display: inline-block;
  display: inline-flex;
  font-weight: 450;
  align-items: center;
  justify-content: flex-start;
  min-height: 2rem;
  white-space: pre-wrap;
  color: ${black};
  // margin-inline-start: 2.5rem;
  ${({ deleted }) =>
    deleted &&
    `
    color: ${red}
`}
  ${(props) =>
    props.archived &&
    !props.draft &&
    `
    color: gray;
  `};
`;

export const VerifyStatus = styled('a')<IVerifyStatusProps>`
  background-color: ${(props) => {
    switch (props.status) {
      case 'complete':
        return green;
      case 'rejected':
        return red;
      default:
        return 'transparent';
    }
  }};
  border: ${(props) =>
    props.status === 'complete' || props.status === 'rejected'
      ? 'none'
      : `2px solid ${green}`};
  border-radius: 50%;
  cursor: pointer;
  display: block;
  height: 1em;
  width: 1em;
`;

export const Verify = styled('div')`
  position: relative;
  height: 1.2em;
  width: 1em;
  margin-inline-start: 0.5em;
`;

export const DateContainer = styled('div')`
  width: 14rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: 500px) {
    flex-direction: row-reverse;
  }
  margin: 3px 0px;
  justify-content: space-between;
  text-align: center;
  @media (max-width: 400px) {
    font-size: 11px;
    width: 11em;
  }
`;

export const ActionWrapper = styled('span')`
  display: inline-flex;
  min-width: 2.5rem;
  height: 24px;
`;

export const TaskItemBody = styled('div')`
  display: inline-block;
  width: 100%;
  justify-content: space-between;
  line-height: 1.8rem;
  padding: 0.5rem 0;
  min-height: 3rem;
  word-break: break-word;
  & > div {
    display: flex;
    align-items: center;
  }
`;

export const Progress = styled('span')<IProgressProps>`
  color: ${(props) => props.color};
  width: 3em;
  text-align: center;
  padding: 0 5px;
  border-left: 1px solid gainsboro;
  box-sizing: content-box;
  font-size: 0.9rem;
`;

export const Pill = styled('span')`
  border-radius: 1em;
  border: ${border1};
  color: #051527;
  display: block;
  font-size: 11px;
  line-height: 1.5em;
  margin: 0 0.5em;
  overflow: hidden;
  padding: 0 0.5em;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 5em;
  &:hover {
    min-width: 5em;
    text-overflow: initial;
    width: auto;
  }
`;

export const TaskBadge = styled(Badge)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
  margin: 0 0.25rem;
  background-color: ${(props) => props.color};
  color: ${white};
  //border: 0.1rem solid ${(props) => props.color};
  padding: 0.4rem 0.4rem;
  text-align: center;
`;

export const TaskBadgeContent = styled.div`
  flex: 1;
  padding: 0 5%;
`;

export const TaskBadgePopOverTaskList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

// for mobile screen
export const MinItemContainer = styled.div`
  display: flex;
  min-height: 10rem;
  align-items: center;
  justify-content: center;
  border-radius: 2rem;
  margin: 1rem 0.75rem;
  overflow-x: scroll;
  box-shadow: ${(props) => props.active && `0 0 1rem ${gray}`};
`;

export const MiniBadgeItemWrapper = styled.div`
  //min-height: 10rem;
  //height: 100%;
  width: 14rem;
  display: flex;
  align-items: center;
  padding: 2.5rem 1rem;
  border-radius: 0 2rem 2rem 0;
  background-image: ${(props) =>
    !props.active
      ? `linear-gradient(to ${props.theme.direction === 'rtl' ? 'left' : 'right'}, ${props.badgeColorType} 0%, var(--exact-white) 50%)`
      : `linear-gradient(to ${props.theme.direction === 'rtl' ? 'left' : 'right'}, ${props.badgeColorType} 0%, var(--exact-white) 100%)`};
  color: var(--exact-white);
  overflow: hidden;
  cursor: ${(props) => (props.isMentioned ? 'pointer' : 'auto')};
`;
export const MiniItemWrapper = styled('li')<IItemWrapperProps>`
  display: block;
  ${(props) => !props.isShowSummery && 'min-height: 10rem'}
  width: ${(props: any) => (props.hasBadge ? 'calc(100% - 3rem)' : '100%')};
  position: relative;
  background-color: ${(props) =>
    props.active ? 'transparent' : 'var(--exact-white)'};
  background-image: ${(props) =>
    props.draft === true
      ? draftBackground
      : props.dependencyNotMeet === true
        ? dependencyNotMeetBackground(props.isDark ? 'black' : 'white')
        : ''};
  border: ${(props) =>
    props.active || !props.hasBadge ? 'none' : `0.1rem solid ${lightGray}`};
  border-radius: ${(props: any) => (props.hasBadge ? '2rem' : '0')};
  margin-inline-start: ${(props: any) => (props.hasBadge ? '-10rem' : '0rem')};
  transition: background-color 0.4s ease;

  ${(props) =>
    !props.hasBadge &&
    `
        border-bottom: 1px solid lightgray;
    `}

  &:hover {
    background-color: transparent;
    ${(props) =>
      props.hasBadge &&
      (props.theme.direction === 'rtl'
        ? `
          border-bottom-right-radius: 0;
          border-top-right-radius: 0;
          border-right: none;
        `
        : `
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
          border-left: none;
        `)}
  }
`;

export const MiniTaskItemBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 0.5rem;
  ${(props) => !props.isShowSummery && 'min-height: 10rem'}
  width: 100%;
`;

export const MiniContentItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.5rem;
  min-height: 2rem;
  flex-wrap: ${(props) => (props.noWrap ? 'nowrap' : 'wrap')};
  gap: ${(props) => (props.noWrap ? '0.5rem' : '')};
`;

export const MinDateContainer = styled.div`
  flex-wrap: wrap;
  //width: 12rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: 500px) {
    flex-direction: row;
  }
  margin: 3px 0px;
  justify-content: space-between;
  text-align: center;
  @media (max-width: 400px) {
    font-size: 11px;
  }
`;

export const MiniContentText = styled.div`
  min-height: 2rem;
  flex: 1;
  flex-wrap: wrap;
  display: flex;
  flex-direction: column;
`;
